import { useState, useEffect, createContext, useCallback, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  initiateSocket,
  subscribeToEvent,
  removeSubscription,
  disconnectSocket,
  connectSocket,
  emitMessage,
} from "../socketio.service";
import { useChatWindowSettings } from "./ChatWindowContext";
import { useConfig } from "./ConfigContext";

const initialState = {
  messages: [],
};

export const SocketContext = createContext(initialState);

function SocketProvider({ children }) {
  const isConnectedRef = useRef(false);
  const messagesRef = useRef(false);
  const connectionId = useRef(null);
  const { config, setConfigInitialized, setConfigOpen } = useConfig();
  const { open } = useChatWindowSettings();

  const [messages, setMessages] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [botMessageLoading, setBotMessageLoading] = useState(false);

  useEffect(() => {
    isConnectedRef.current = isConnected;
    if (isConnected && !messages) {
      getHistorySnapshot();
    }
  }, [isConnected]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if(!open || isConnected) return;
    initSocket();
  }, [open, isConnected]);

  useEffect(() => {
    return () => {
      detachListeners();
    };
  }, []);

  const filterAndSortMessages = (upcomingMessages, messageHistory = []) => {
    const allMessages = [...upcomingMessages, ...messageHistory];

    const messageMap = new Map();

    for (const message of allMessages) {
      if (message.index !== undefined && message.index !== null) {
        messageMap.set(message.index, message);
      }
    }

    return Array.from(messageMap.values()).sort((a, b) => a.index - b.index);
  };

  const updateMessageWithToken = (event, isFinal) => {
    if (!isFinal && !isStreaming) {
      setIsStreaming(true);
    }
    if (messagesRef.current?.messages?.length) {
      const targetId = isFinal
        ? event.updated_item?.timestamp
        : event.message_id;

      const messageToUpdateIndex = messagesRef.current.messages.findIndex(
        (message) => message.timestamp === targetId
      );

      if (messageToUpdateIndex === -1) return;
      const updatedMessage = {
        ...messagesRef?.current?.messages?.[messageToUpdateIndex],
        payload: isFinal
          ? event.updated_item?.payload
          : JSON.stringify(event.message),
      };
      const updatedMessages = [...messagesRef.current.messages];
      updatedMessages.splice(messageToUpdateIndex, 1, updatedMessage);
      saveChatHistory(updatedMessages);

      if (isFinal) {
        setIsStreaming(false);
        setBotMessageLoading(false);
      }
    }
  };

  const saveChatHistory = (history) => {
    setMessages({ ...messagesRef.current, messages: history });
  };

  const updateHistory = (snapshot) => {
    let newList = snapshot.messages ?? snapshot.history_snapshot.messages;
    if (messagesRef.current?.messages?.length) {
      newList = filterAndSortMessages(newList, messagesRef.current?.messages);
    }
    if(newList.length === 0 && !config.metadataOptions) {
      getWelcomeMessage();
    }
    setMessages({ ...snapshot.history_snapshot, messages: newList });
    setHistoryLoading(false);
    setBotMessageLoading(false);
  };

  function socketStatusCallback(response) {

    if (!response.success) {
      setIsStreaming(false);
      setBotMessageLoading(false);

      const errorMessage = {
        origin: "error",
        message: response.message ?? "Error occurred",
      };

      const updatedMessages = messagesRef.current
        ? messagesRef.current.messages.filter((msg) => msg.origin !== "loader")
        : [];

      saveChatHistory([...updatedMessages, errorMessage]);
    }
  }

  function emitSocketMessage(key, payload = {}) {
    emitMessage(
      key,
      {
        connection_id: connectionId.current,
        app_id: config.appId,
        metadata: Object.keys(config.metadataOptions ?? {}).reduce(
          (acc, key) => {
            acc[key] =
              config.metadataOptions[key].value ??
              config.metadataOptions[key].defaultOption;
            return acc;
          },
          {}
        ),
        ...payload,
      },
      socketStatusCallback
    );
  }

  function onConnect() {
    setIsConnected(true);
  }

  function getHistorySnapshot(lastMessageTs, snapshotSize) {
    emitSocketMessage("request_history_snapshot", {
      last_message_ts: lastMessageTs,
      snapshot_size: snapshotSize,
    });
  }

  function onDisconnect(reason, details) {
    setIsConnected(false);
  }

  function getWelcomeMessage() {
    emitSocketMessage("request_welcome_message");
  }

  function handleHistoryLoad(snapshot) {
    updateHistory(snapshot);
  }

  function handleIncomingTokenStream(event) {
    updateMessageWithToken(event);
  }

  function handleFinalMessageUpdate(event) {
    updateMessageWithToken(event, true);
  }

  function reconnectSocket(e) {
    if (!e.target.hidden && !isConnectedRef.current) {
      connectSocket();
    }
  }

  const resetSession = useCallback(() => {
    messagesRef.current.messages = [];
    emitSocketMessage("reset_session");
    emitSocketMessage("clear_history");
  }, [config, emitSocketMessage]);

  async function establishConnection() {
    let currentSession =
      JSON.parse(localStorage.getItem("webchat_session")) || {};

    const shouldResetSession =
      !currentSession.connectionId ||
      currentSession.expires < Date.now() ||
      currentSession.appId !== config.appId;

    if (shouldResetSession) {
      currentSession = {
        ...currentSession,
        connectionId: uuidv4(),
        appId: config.appId,
        expires: Date.now() + 1000 * 60 * 60 * 3,
      };

      localStorage.setItem("webchat_session", JSON.stringify(currentSession));
      setConfigInitialized(false);
      setConfigOpen(true);
    }

    connectionId.current = currentSession.connectionId;

    await initiateSocket({
      connection_id: currentSession.connectionId,
      app_id: config.appId,
    });
  }

  const initSocket = useCallback(async () => {
    await establishConnection();
    subscribeToEvent("connect", onConnect);
    subscribeToEvent("disconnect", onDisconnect);
    subscribeToEvent("send_history_snapshot", handleHistoryLoad);
    subscribeToEvent("stream_message", handleIncomingTokenStream);
    subscribeToEvent("update_history_item", handleFinalMessageUpdate);
    document.addEventListener("visibilitychange", reconnectSocket);
  }, []);

  const detachListeners = () => {
    removeSubscription("disconnect", onDisconnect);
    removeSubscription("send_history_snapshot", handleHistoryLoad);
    removeSubscription("stream_message", handleIncomingTokenStream);
    removeSubscription("update_history_item", handleFinalMessageUpdate);
    document.removeEventListener("visibilitychange", reconnectSocket);
    disconnectSocket();
  };

  return (
    <SocketContext.Provider
      value={{
        getWelcomeMessage,
        getHistorySnapshot,
        setHistoryLoading,
        setBotMessageLoading,
        emitMessage: emitSocketMessage,
        saveChatHistory,
        isConnected,
        isStreaming,
        isLoading: botMessageLoading,
        isLoadingHistory: historyLoading,
        messageHistory: messages,
        resetSession,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
}

export default SocketProvider;
